import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Switch, Tooltip, CircularProgress, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { SettingsService } from '../../../api/services/settingsService';
import EditRecipientsModal from '../../../components/EditRecipientsModal';
import { extractTotalCount } from '../../../components/common/tableStyles';

export default function AlertRulesTab({ setNotification, isActive }) {
    const [triggers, setTriggers] = useState([]);
    const [triggersLoading, setTriggersLoading] = useState(false);
    const [triggersError, setTriggersError] = useState(null);

    const [rulesPage, setRulesPage] = useState(0);
    const [rulesRowsPerPage, setRulesRowsPerPage] = useState(20);
    const [totalRules, setTotalRules] = useState(0);

    const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
    const [editingTriggerRoles, setEditingTriggerRoles] = useState(null);
    const [recipientsLoading, setRecipientsLoading] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchTriggers();
        }
    }, [isActive, rulesPage, rulesRowsPerPage]);

    const fetchTriggers = async () => {
        setTriggersLoading(true);
        setTriggersError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const params = {
                page: rulesPage,
                limit: rulesRowsPerPage
            };
            const data = await SettingsService.getNotificationTriggers(tenantId, params);
            const triggersArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            setTriggers(triggersArray);
            setTotalRules(extractTotalCount(data, triggersArray));
        } catch (err) {
            console.error('Failed to fetch triggers:', err);
            setTriggersError('Failed to load alert rules.');
        } finally {
            setTriggersLoading(false);
        }
    };

    const handleToggleTrigger = async (triggerId, currentStatus) => {
        const newStatus = !currentStatus;
        const previousTriggers = [...triggers];
        
        setTriggers(triggers.map(t => t.id === triggerId ? { ...t, isActive: newStatus } : t));
        
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await SettingsService.updateNotificationTrigger(triggerId, { isActive: newStatus }, tenantId);
            setNotification({ open: true, message: 'Alert rule updated successfully', severity: 'success' });
        } catch (err) {
            console.error('Failed to toggle trigger:', err);
            setTriggers(previousTriggers);
            setNotification({ open: true, message: 'Failed to update alert rule', severity: 'error' });
        }
    };

    const handleToggleChannel = async (triggerId, channel, currentChannels) => {
        const previousTriggers = [...triggers];
        let newChannels = [...currentChannels];
        
        if (newChannels.includes(channel)) {
            newChannels = newChannels.filter(c => c !== channel);
        } else {
            newChannels.push(channel);
        }
        
        setTriggers(triggers.map(t => t.id === triggerId ? { ...t, channels: newChannels } : t));
        
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await SettingsService.updateNotificationTrigger(triggerId, { channels: newChannels }, tenantId);
            setNotification({ open: true, message: 'Delivery channels updated', severity: 'success' });
        } catch (err) {
            console.error('Failed to toggle channel:', err);
            setTriggers(previousTriggers);
            setNotification({ open: true, message: 'Failed to update delivery channels', severity: 'error' });
        }
    };

    const handleSaveRecipients = async ({ recipientRoles, recipientEmails }) => {
        setRecipientsLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            
            await SettingsService.updateNotificationTrigger(editingTriggerRoles.id, { recipientRoles, recipientEmails }, tenantId);
            
            setTriggers(triggers.map(t => 
                t.id === editingTriggerRoles.id ? { ...t, recipientRoles, recipientEmails } : t
            ));
            
            setNotification({ open: true, message: 'Recipients updated successfully', severity: 'success' });
            setRecipientsModalOpen(false);
        } catch (err) {
            console.error('Failed to update recipients:', err);
            setNotification({ open: true, message: 'Failed to update recipients', severity: 'error' });
        } finally {
            setRecipientsLoading(false);
        }
    };

    if (triggersLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                <CircularProgress />
                <Typography color="text.secondary">Fetching rules...</Typography>
            </Box>
        );
    }

    if (triggersError) {
        return <Alert severity="error">{triggersError}</Alert>;
    }

    return (
        <Box>
            {triggers.length > 0 ? (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Event & Description</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Recipients</TableCell>
                                <TableCell sx={{ width: 160 }}>Channels</TableCell>
                                <TableCell sx={{ textAlign: 'center', width: 100 }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {triggers.map((rule) => (
                                <TableRow key={rule.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ maxWidth: 300 }}>
                                        <Typography variant="body1" fontWeight="bold" sx={{ textTransform: 'capitalize', mb: 0.5 }}>
                                            {rule.eventName?.replace(/_/g, ' ').toLowerCase()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {rule.description || `Automated notifications for ${rule.eventName} events.`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={rule.priority} 
                                            size="small" 
                                            color={rule.priority === 'High' ? 'error' : rule.priority === 'Medium' ? 'warning' : 'success'}
                                            sx={{ height: 22, fontWeight: 'bold', fontSize: '0.7rem' }} 
                                        />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 350 }}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                                            {rule.recipientRoles?.slice(0, 2).map((role) => (
                                                <Chip key={role} label={role} size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: 'primary.50', color: 'primary.main', fontWeight: 'bold' }} />
                                            ))}
                                            {rule.recipientRoles?.length > 2 && (
                                                <Tooltip title={rule.recipientRoles.slice(2).join(', ')}>
                                                    <Chip label={`+${rule.recipientRoles.length - 2} Roles`} size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: 'rgba(0,0,0,0.08)', color: 'text.secondary', fontWeight: 'bold', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                            {rule.recipientEmails?.slice(0, 2).map((email) => (
                                                <Tooltip title={email} key={email}>
                                                    <Chip label={email.split('@')[0]} size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: 'secondary.50', color: 'secondary.main', fontWeight: 'bold' }} />
                                                </Tooltip>
                                            ))}
                                            {rule.recipientEmails?.length > 2 && (
                                                <Tooltip title={rule.recipientEmails.slice(2).join(', ')}>
                                                    <Chip label={`+${rule.recipientEmails.length - 2} Emails`} size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: 'rgba(0,0,0,0.08)', color: 'text.secondary', fontWeight: 'bold', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                            {(!rule.recipientRoles?.length && !rule.recipientEmails?.length) && (
                                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', py: 0.5 }}>No recipients assigned</Typography>
                                            )}
                                            <IconButton 
                                                size="small" 
                                                onClick={() => { setEditingTriggerRoles(rule); setRecipientsModalOpen(true); }}
                                                sx={{ p: 0.5, ml: 0.5, bgcolor: 'rgba(0,0,0,0.04)', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}
                                            >
                                                <EditIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            {['InApp', 'Email', 'SMS'].map((ch) => {
                                                const isEnabled = rule.channels?.includes(ch);
                                                return (
                                                    <Tooltip title={`${ch} Delivery`} key={ch}>
                                                        <Box 
                                                            onClick={() => handleToggleChannel(rule.id, ch, rule.channels || [])}
                                                            sx={{ 
                                                                cursor: 'pointer', 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                width: 32, 
                                                                height: 32, 
                                                                borderRadius: '50%',
                                                                bgcolor: isEnabled ? 'primary.main' : 'action.disabledBackground',
                                                                color: isEnabled ? 'white' : 'text.disabled',
                                                                transition: 'all 0.2s',
                                                                '&:hover': { transform: 'scale(1.1)' }
                                                            }}
                                                        >
                                                            {ch === 'Email' ? <EmailIcon fontSize="small" /> : ch === 'InApp' ? <NotificationsActiveIcon fontSize="small" /> : <SmsIcon fontSize="small" />}
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch 
                                            checked={rule.isActive} 
                                            onChange={() => handleToggleTrigger(rule.id, rule.isActive)}
                                            size="small" 
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50]}
                        component="div"
                        count={triggers.length === 0 ? 0 : totalRules}
                        rowsPerPage={rulesRowsPerPage}
                        page={rulesPage}
                        onPageChange={(e, newPage) => setRulesPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRulesRowsPerPage(parseInt(e.target.value, 10));
                            setRulesPage(0);
                        }}
                    />
                </TableContainer>
            ) : (
                <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.01)', borderRadius: 3, border: '1px dashed #ddd' }}>
                    <NotificationsActiveIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">No notification rules found.</Typography>
                    <Typography variant="body2" color="text.disabled">Add your first automation rule to get started.</Typography>
                </Box>
            )}

            <EditRecipientsModal 
                open={recipientsModalOpen}
                onClose={() => setRecipientsModalOpen(false)}
                onSave={handleSaveRecipients}
                editingTrigger={editingTriggerRoles}
                loading={recipientsLoading}
            />
        </Box>
    );
}
